import { useEffect, useState } from 'react';
import type { SideListItem } from '@/core/models/practice.models';
import { practiceService } from '@/core/services/practice';
import { selectClient, type SelectedClient } from '@/core/selectedClient';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';

/**
 * The client bar's switcher: the app-wide selected client, changed from the top of every Accounts screen. Picking one
 * switches it everywhere (the shell then reloads the screen for that client), so the menus on the left always show the
 * data of the client chosen here.
 */
export function ClientSwitcher({ client }: { client: SelectedClient }) {
  const [clients, setClients] = useState<SideListItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    practiceService.getClientSideList(1, 1000, '').then(
      (response) => {
        if (!cancelled) setClients(response?.sideModuleList ?? []);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // The open client is always in the list, even before (or without) the list loading.
  const items = clients.some((c) => c.id === client.id)
    ? clients
    : [{ id: client.id, universalId: client.id, name: client.name, value: null }, ...clients];

  return (
    <Select<SideListItem>
      id="client-dropdown"
      data-testid="client-dropdown"
      aria-label="client dropdown"
      className="client-switcher"
      placeholder="Switch Client"
      items={items}
      bindLabel="name"
      bindValue="id"
      searchable
      clearable={false}
      openOnFocus
      value={client.id}
      renderLabel={(item) => (
        <span className="ng-value-label">
          <span title={item.name}>
            <Icon name="domain" outlined />
            {item.name}
          </span>
        </span>
      )}
      onChange={(value, item) => {
        const found = item ?? items.find((c) => c.id === value);
        if (found && found.id !== client.id) selectClient(found.id, found.name);
      }}
    />
  );
}
